import { Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty, keys } from 'lodash-es';
import React, { FC, useState, useEffect, useMemo } from 'react';
import { UseFormWatch, FieldValues } from 'react-hook-form';
import { IMappingFormSettingsResponse } from 'wisestep-api';

import RenderTextInput from '../../components/PersonalInfo/RenderTextInput';
import { WsDatePicker } from '../../components/WsDatePicker';
import {
  willingToTravelList,
  noticePeriodInDaysList,
  authorizationCategoryList,
  communicationPreferenceList,
  jobPreferenceList,
  currentJobTypeList,
  usVisaIndependentList,
  usVisaDependentList,
  germanyVisaIndependentList,
  germanyVisaDependentList,
  ukVisaIndependentList,
  ukVisaDependentList,
  polandVisaIndependentList,
} from '../../helper/ConversationFieldButtonGroups';
import ISubmissionSettingContext from '../../types/ISubmissionSettingContext';
import ISubmissionSettingsResponse from '../../types/ISubmissionSettingsResponse';
import ControlledTextInput from '../ControlledTextInput/ControlledTextInput';
import ControlledWsButtonGroup from '../ControlledWsButtonGroup/ControlledWsButtonGroup';
import { Item } from '../Item';
import { Label } from '../Label';
import TagsInput from '../TagsInput/TagsInput';
import WsDateTimePicker from '../WsDateTimePicker/WsDateTimePicker';
import { WsLocationSearch } from '../WsLocationSearch';
import WsSelect from '../WsSelect/WsSelect';
import { WsTypoColors, WsTypography, WsTypoVariants } from '../WsTypography';

import { CountryOptions, visaOptionsByCountry } from './visaOptionsHelper';

const GridItem = styled(Item)(() => ({
  paddingTop: 0,
  paddingBottom: '10px',
}));

const FormTagsInput = styled(TagsInput)(() => ({
  '.MuiOutlinedInput-root': {
    padding: '4.5px 8px',
    margin: 0,
  },
}));

export interface IJobPreference {
  showTitle?: boolean;
  purpose?: 'mapping' | 'submission';
  submissionSetting?: ISubmissionSettingsResponse | ISubmissionSettingContext | null;
  mappingFormSettings?: IMappingFormSettingsResponse;
  watch: UseFormWatch<FieldValues>;
}

export interface optionsVisa {
  label: string;
  value: string;
  additionalInfo: string;
}

const JobPreference: FC<IJobPreference> = ({
  showTitle = true,
  purpose = 'submission',
  submissionSetting,
  mappingFormSettings,
  watch,
}) => {
  const [anywhere, setAnywhere] = useState<{ index: number; key: string } | null>(null);
  const travelType = [
    {
      label: 'Travel is 25 %',
      value: 'TRAVEL_0_25',
    },
    {
      label: 'Travel is 50 %',
      value: 'TRAVEL_50',
    },
    {
      label: 'Travel is 75 %',
      value: 'TRAVEL_75',
    },
    {
      label: 'Travel is 100 %',
      value: 'TRAVEL_100',
    },
  ];

  const relocationPreferencesType = [
    {
      label: 'No Relocation',
      value: 'NoRelocation',
    },
    {
      label: 'Within State',
      value: 'WithinState',
    },
    {
      label: 'Within Country',
      value: 'WithinCountry',
    },
    {
      label: 'Any Where',
      value: 'AnyWhere',
    },
  ];
  const [authorizationCategory, setAuthorizationCategory] = useState<{
    index: number;
    key: string;
  } | null>(null);
  const relocationField = watch('willingToTravel');
  const relocationListField = watch('relocationList');
  const authorizationCategoryField = watch('authorizationCategory');
  const currentJobIncludesTravelField = watch('currentJobIncludesTravel');
  const [authorizationList, setAuthorizationList] = useState<string[]>([]);
  const currentLocation = watch('currentLocation');
  const selectedCountry = watch('workAuthorization');
  const selectedVisaOption = watch('visaStatus');

  const getVisaOptionsForCountry = (countryValue: string) => {
    if (!countryValue) return [];
    const countryOptions = visaOptionsByCountry.find(
      (entry) => entry.country.toUpperCase() === countryValue.toUpperCase()
    );
    return countryOptions
      ? countryOptions.options.map(({ label, value }) => ({ label, value }))
      : [];
  };

  const visaOption = useMemo(() => {
    if (!selectedCountry || selectedCountry === 'NOT_SPECIFIED') {
      return [];
    }
    return getVisaOptionsForCountry(selectedCountry);
  }, [selectedCountry]);

  const isOtherVisaOptionSelected = selectedVisaOption === 'Other';

  useEffect(() => {
    if (!isEmpty(currentLocation)) {
      if (currentLocation[0]?.country == 'Germany') {
        switch (authorizationCategoryField?.key) {
          case 'Visa Independent':
            setAuthorizationList(keys(germanyVisaIndependentList));
            break;
          case 'Visa Dependent':
            setAuthorizationList(keys(germanyVisaDependentList));
            break;
          default:
            setAuthorizationList([]);
        }
      } else if (currentLocation[0]?.country == 'United States') {
        switch (authorizationCategoryField?.key) {
          case 'Visa Independent':
            setAuthorizationList(keys(usVisaIndependentList));
            break;
          case 'Visa Dependent':
            setAuthorizationList(keys(usVisaDependentList));
            break;
          default:
            setAuthorizationList([]);
        }
      } else if (currentLocation[0]?.country == 'United Kingdom') {
        switch (authorizationCategoryField?.key) {
          case 'Visa Independent':
            setAuthorizationList(keys(ukVisaIndependentList));
            break;
          case 'Visa Dependent':
            setAuthorizationList(keys(ukVisaDependentList));
            break;
          default:
            setAuthorizationList([]);
        }
      } else if (currentLocation[0]?.country == 'Poland') {
        switch (authorizationCategoryField?.key) {
          case 'Visa Independent':
            setAuthorizationList(keys(polandVisaIndependentList));
            break;
          default:
            setAuthorizationList([]);
        }
      }
    }
  }, [authorizationCategoryField, currentLocation]);

  useEffect(() => {
    relocationListField && setAnywhere(relocationListField);
    authorizationCategoryField && setAuthorizationCategory(authorizationCategoryField);
  }, [relocationField, authorizationCategoryField, relocationListField]);

  const formLabel = (label: string, isRequired: boolean) => label + (isRequired ? '*' : '');
  const workAuthValidity = (submissionSetting as ISubmissionSettingsResponse)?.workAuthValidity;
  return (
    <>
      {showTitle && (
        <GridItem sx={{ mt: '12px' }}>
          <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.PRIMARY}>
            Job Preferences
          </WsTypography>
        </GridItem>
      )}
      {purpose == 'submission' && (
        <>
          {!submissionSetting?.currentJobIncludeTravel?.hidden && (
            <>
              <GridItem id="currentJobIncludesTravel">
                <ControlledWsButtonGroup
                  name="currentJobIncludesTravel"
                  inputTitle="Current Job Includes Travel"
                  showMandatorySymbol={submissionSetting?.currentJobIncludeTravel?.required}
                  buttonsList={['Yes', 'No']}
                />
              </GridItem>
              {currentJobIncludesTravelField?.key == 'Yes' && (
                <GridItem>
                  <Grid xs={8.5}>
                    <WsSelect
                      name="currentJobTravelType"
                      label=""
                      selectArray={travelType}
                      placeholder="Enter traveling percentage"
                    />
                  </Grid>
                </GridItem>
              )}
            </>
          )}
          {!submissionSetting?.willingToTravel?.hidden && (
            <>
              <GridItem id="willingToTravel">
                <ControlledWsButtonGroup
                  name="willingToTravel"
                  inputTitle="Willing to travel"
                  showMandatorySymbol={submissionSetting?.willingToTravel?.required}
                  buttonsList={keys(willingToTravelList)}
                  defaultValue={relocationField}
                />
              </GridItem>
              {(relocationField?.index == 0 || relocationField == 0) && (
                <>
                  <GridItem>
                    <Grid item xs={8.5}>
                      <WsLocationSearch
                        placeHolder="Enter preferred city"
                        name="preferredLocation"
                      />
                    </Grid>
                  </GridItem>
                  <GridItem>
                    <Grid item xs={8.5}>
                      <WsSelect
                        name="willingToTravelType"
                        label=""
                        selectArray={travelType}
                        placeholder="Enter traveling percentage"
                      />
                    </Grid>
                  </GridItem>
                </>
              )}
            </>
          )}
          {!submissionSetting?.familyWillingToRelocate?.hidden && (
            <GridItem id="familyWillingToRelocate">
              <ControlledWsButtonGroup
                name="familyWillingToRelocate"
                inputTitle="Family willing to relocate"
                showMandatorySymbol={submissionSetting?.familyWillingToRelocate?.required}
                buttonsList={['Yes', 'No']}
              />
            </GridItem>
          )}
          {!submissionSetting?.relocationPreferences?.hidden && (
            <GridItem>
              <Grid item xs={8.5}>
                <Label>
                  {formLabel(
                    'Relocation Preference ',
                    submissionSetting?.relocationPreferences?.required ?? false
                  )}
                </Label>
                <WsSelect
                  name="relocationPreferences"
                  label=""
                  selectArray={relocationPreferencesType}
                  placeholder="Enter relocation preference"
                />
              </Grid>
            </GridItem>
          )}

          {!submissionSetting?.availableTimeSlot?.hidden && (
            <GridItem>
              <Grid item xs={8.5}>
                <Label>
                  {formLabel(
                    'Available From ',
                    submissionSetting?.availableTimeSlot?.required ?? false
                  )}
                </Label>
                <WsDatePicker
                  name="availableFrom"
                  label=""
                  size="small"
                  required={submissionSetting?.availableTimeSlot?.required}
                />
              </Grid>
            </GridItem>
          )}

          {!submissionSetting?.noticePeriod?.hidden && (
            <GridItem>
              <ControlledWsButtonGroup
                name="noticePeriodInDays"
                inputTitle="Notice Period"
                showMandatorySymbol={submissionSetting?.noticePeriod?.required}
                buttonsList={keys(noticePeriodInDaysList)}
              />
            </GridItem>
          )}
          {/* {!submissionSetting?.workAuthorizationCategory?.hidden && (
            <>
              <GridItem sx={{ mt: '12px' }} id="authorizationCategory">
                <ControlledWsButtonGroup
                  name="authorizationCategory"
                  inputTitle="Work Authorization Category"
                  showMandatorySymbol={submissionSetting?.workAuthorizationCategory?.required}
                  buttonsList={keys(authorizationCategoryList)}
                />
              </GridItem>
              <GridItem sx={{ mt: '12px' }} id="authorization">
                <ControlledWsButtonGroup
                  name="authorization"
                  inputTitle=""
                  buttonsList={authorizationList}
                />
              </GridItem>
              {authorizationCategory?.key &&
                authorizationCategory?.key == 'Others' &&
                submissionSetting?.workAuthorization?.required && (
                  <RenderTextInput
                    name="workAuthorization"
                    labelText="Work Authorization *"
                    placeholder="Work authorization"
                  />
                )}
            </>
          )} */}
          {!submissionSetting?.workAuthorization?.hidden && (
            <>
              <GridItem>
                <Grid item xs={8.5}>
                  {
                    <WsSelect
                      name="workAuthorization"
                      label={formLabel(
                        'Authorized Country to Work',
                        submissionSetting?.workAuthorization?.required ?? false
                      )}
                      selectArray={CountryOptions}
                      defaultValue={''}
                    />
                  }
                </Grid>
                <Grid item xs={8.5}>
                  <WsSelect
                    name="visaStatus"
                    label={formLabel('Visa Status', submissionSetting?.visaType?.required ?? false)}
                    selectArray={visaOption}
                    disabled={!selectedCountry || selectedCountry === 'NOT_SPECIFIED'}
                    defaultValue={''}
                  />
                </Grid>
              </GridItem>
            </>
          )}
          {
            <>
              {isOtherVisaOptionSelected && (
                <RenderTextInput
                  name="visaStatusOther"
                  labelText={formLabel(
                    'Visa Status Other ',
                    submissionSetting?.visaStatusOther?.required ?? true
                  )}
                  placeholder="Enter Visa Notes"
                />
              )}
            </>
          }

          {workAuthValidity && !workAuthValidity?.hidden && (
            <GridItem>
              <Grid item xs={8.5}>
                <Label>
                  {formLabel('Work Authorization Validity', workAuthValidity.required ?? false)}
                </Label>
                <WsDatePicker
                  name="workAuthValidity"
                  label=""
                  size="small"
                  required={workAuthValidity.required}
                />
              </Grid>
            </GridItem>
          )}
          {!submissionSetting?.nationality?.hidden && (
            <RenderTextInput
              name="nationality"
              labelText={formLabel(
                'Nationality ',
                submissionSetting?.nationality?.required ?? false
              )}
              placeholder="Enter Country"
            />
          )}
          {/* {!submissionSetting?.visaType?.hidden && (
            <RenderTextInput
              name="visaStatus"
              labelText={formLabel('Visa Status ', submissionSetting?.visaType?.required ?? false)}
              placeholder="Enter visa status"
            />
          )} */}
          {!submissionSetting?.visaValidity?.hidden && (
            <RenderTextInput
              name="visaValidity"
              labelText={formLabel(
                'Visa Validity ',
                submissionSetting?.visaValidity?.required ?? false
              )}
              placeholder="Enter visa validity"
            />
          )}
          {!submissionSetting?.desiredJobTitles?.hidden && (
            <RenderTextInput
              name="desiredJobTitles"
              labelText={formLabel(
                'Prefered Job Title ',
                submissionSetting?.desiredJobTitles?.required ?? false
              )}
              placeholder="Enter Prefered Job Title"
            />
          )}
          {!submissionSetting?.communicationPreferences?.hidden && (
            <GridItem id="communicationPreference">
              <ControlledWsButtonGroup
                name="communicationPreference"
                inputTitle="Communication Preference"
                showMandatorySymbol={submissionSetting?.communicationPreferences?.required}
                multiple={true}
                buttonsList={keys(communicationPreferenceList)}
              />
            </GridItem>
          )}
          {!submissionSetting?.jobPreferences?.hidden && (
            <GridItem id="jobPreference">
              <ControlledWsButtonGroup
                name="jobPreference"
                inputTitle="Job Preference"
                showMandatorySymbol={submissionSetting?.jobPreferences?.required}
                multiple={true}
                buttonsList={keys(jobPreferenceList)}
              />
            </GridItem>
          )}
          {!submissionSetting?.jobType?.hidden && (
            <GridItem id="currentJobType">
              <ControlledWsButtonGroup
                name="currentJobType"
                inputTitle="Current Job Type"
                showMandatorySymbol={submissionSetting?.jobType?.required}
                buttonsList={keys(currentJobTypeList)}
              />
            </GridItem>
          )}
        </>
      )}
      {purpose == 'mapping' && (
        <>
          {mappingFormSettings?.jobIncludesTravel.show && (
            <GridItem id="currentJobIncludesTravel">
              <ControlledWsButtonGroup
                name="currentJobIncludesTravel"
                inputTitle="Current Job Includes Travel"
                showMandatorySymbol={mappingFormSettings?.jobIncludesTravel?.mandatory}
                buttonsList={['Yes', 'No']}
              />
            </GridItem>
          )}
          {mappingFormSettings?.willingToTravel.show && (
            <>
              <GridItem sx={{ mt: '12px' }} id="willingToTravel">
                <ControlledWsButtonGroup
                  name="willingToTravel"
                  inputTitle="Willing to Travel"
                  showMandatorySymbol={mappingFormSettings?.willingToTravel?.mandatory}
                  buttonsList={keys(willingToTravelList)}
                  defaultValue={relocationField}
                />
                {(relocationField?.index == 0 || relocationField == 0) && (
                  <>
                    {anywhere?.key != 'Anywhere' && (
                      <GridItem>
                        <WsLocationSearch
                          placeHolder="Enter preferred city"
                          name="preferredLocation"
                        />
                      </GridItem>
                    )}
                    <GridItem>
                      <WsSelect
                        name="willingToTravelType"
                        label=""
                        selectArray={travelType}
                        placeholder="Enter traveling percentage"
                        required={mappingFormSettings?.willingToTravel.mandatory}
                      />
                    </GridItem>
                  </>
                )}
              </GridItem>
            </>
          )}
          {mappingFormSettings?.familyWillingToRelocate.show && (
            <GridItem id="familyWillingToRelocate">
              <ControlledWsButtonGroup
                name="familyWillingToRelocate"
                inputTitle="Family willing to relocate"
                showMandatorySymbol={mappingFormSettings?.familyWillingToRelocate?.mandatory}
                buttonsList={['Yes', 'No']}
              />
            </GridItem>
          )}
          {mappingFormSettings?.relocationPreference?.show && (
            <GridItem>
              <Label>
                {`Relocation preference 
              ${mappingFormSettings?.relocationPreference?.mandatory ? '*' : ''}`}
              </Label>
              <WsSelect
                name="relocationPreferences"
                label=""
                selectArray={relocationPreferencesType}
              />
            </GridItem>
          )}
          {mappingFormSettings?.noticePeriodInDays.show && (
            <GridItem>
              <Grid container spacing={2}>
                <Grid item lg={4.5}>
                  <Label>
                    {formLabel('Available From', mappingFormSettings?.availableFrom.mandatory)}
                  </Label>
                  <WsDateTimePicker
                    name="availableFrom"
                    label=""
                    size="small"
                    required={mappingFormSettings?.availableFrom.mandatory}
                  />
                </Grid>
                <Grid item lg={7.5} id="noticePeriodInDays">
                  <ControlledWsButtonGroup
                    name="noticePeriodInDays"
                    inputTitle="Notice Period"
                    buttonsList={keys(noticePeriodInDaysList)}
                    showMandatorySymbol={mappingFormSettings?.noticePeriodInDays.mandatory}
                  />
                </Grid>
              </Grid>
            </GridItem>
          )}
          {mappingFormSettings?.workAuthorizationCategory.show && (
            <GridItem sx={{ mt: '12px' }} id="authorizationCategory">
              <ControlledWsButtonGroup
                name="authorizationCategory"
                inputTitle="Work Authorization Category"
                buttonsList={keys(authorizationCategoryList)}
                showMandatorySymbol={mappingFormSettings?.workAuthorizationCategory.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.authorization.show && (
            <GridItem sx={{ mt: '12px' }} id="authorization">
              <ControlledWsButtonGroup
                name="authorization"
                inputTitle=""
                buttonsList={authorizationList}
                showMandatorySymbol={false}
              />
            </GridItem>
          )}
          {authorizationCategory?.key &&
            authorizationCategory?.key == 'Others' &&
            mappingFormSettings?.authorization.show && (
              <>
                <GridItem>
                  <ControlledTextInput
                    name="workAuthorization"
                    labelText={formLabel(
                      'Work Authorization',
                      mappingFormSettings?.workauthorization.mandatory
                    )}
                    type="text"
                    fullWidth
                    id="workAuthorization"
                    placeholder="Work authorization"
                    variant="outlined"
                    autoComplete="workAuthorization"
                    required={mappingFormSettings?.workauthorization.mandatory}
                  />
                </GridItem>
              </>
            )}
          {mappingFormSettings?.nationality.show && (
            <GridItem>
              <ControlledTextInput
                name="nationality"
                labelText={formLabel('Nationality', mappingFormSettings?.nationality.mandatory)}
                type="text"
                fullWidth
                id="citizenship"
                placeholder="Enter Country"
                variant="outlined"
                autoComplete="citizenship"
                required={mappingFormSettings?.nationality.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.visaStatus.show && (
            <GridItem>
              <ControlledTextInput
                name="visaStatus"
                labelText="Visa Status"
                showMandatorySymbol={mappingFormSettings?.visaStatus?.mandatory}
                type="text"
                fullWidth
                id="visaStatus"
                placeholder="Enter visa status"
                variant="outlined"
                autoComplete="marginTop"
              />
            </GridItem>
          )}
          {mappingFormSettings?.visaValidity.show && (
            <GridItem>
              <ControlledTextInput
                name="visaValidity"
                labelText="Visa Validity"
                showMandatorySymbol={mappingFormSettings?.visaValidity?.mandatory}
                type="text"
                fullWidth
                id="visaValidity"
                placeholder="Enter visa validity"
                variant="outlined"
                autoComplete="marginTop"
              />
            </GridItem>
          )}
          {mappingFormSettings?.tags.show && (
            <GridItem>
              <Label>{formLabel('Tags', mappingFormSettings?.tags.mandatory)}</Label>
              <FormTagsInput
                name="tags"
                placeholder="Enter Tags"
                multiple={true}
                initialValue={[]}
                required={mappingFormSettings?.tags.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.source.show && (
            <GridItem>
              <Label>{formLabel('Source', mappingFormSettings?.source.mandatory)}</Label>
              <ControlledTextInput
                style={{ marginTop: '12px' }}
                name="source"
                labelText=""
                type="text"
                fullWidth
                id="source"
                placeholder="Enter Source"
                variant="outlined"
                autoComplete="source"
                required={mappingFormSettings?.source.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.desiredJobTitles?.show && (
            <GridItem>
              <Label>
                {formLabel('Prefered Job Title', mappingFormSettings?.desiredJobTitles.mandatory)}
              </Label>
              <ControlledTextInput
                style={{ marginTop: '12px' }}
                name="desiredJobTitles"
                labelText=""
                type="text"
                fullWidth
                id="desiredJobTitles"
                placeholder="Enter Prefered Job Title"
                variant="outlined"
                autoComplete="desiredJobTitles"
                required={mappingFormSettings?.desiredJobTitles?.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.communicationPreference.show && (
            <GridItem id="communicationPreference">
              <ControlledWsButtonGroup
                name="communicationPreference"
                inputTitle="Communication Preference"
                multiple={true}
                buttonsList={keys(communicationPreferenceList)}
                showMandatorySymbol={mappingFormSettings?.communicationPreference.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.jobPreference.show && (
            <GridItem id="jobPreference">
              <ControlledWsButtonGroup
                name="jobPreference"
                inputTitle="Job Preference"
                multiple={true}
                buttonsList={keys(jobPreferenceList)}
                showMandatorySymbol={mappingFormSettings?.jobPreference.mandatory}
              />
            </GridItem>
          )}
          {mappingFormSettings?.currentJobType.show && (
            <GridItem id="currentJobType">
              <ControlledWsButtonGroup
                name="currentJobType"
                inputTitle="Current Job Type"
                buttonsList={keys(currentJobTypeList)}
                showMandatorySymbol={mappingFormSettings?.currentJobType.mandatory}
              />
            </GridItem>
          )}
        </>
      )}
    </>
  );
};

export default JobPreference;
